import React from "react";
import { Link } from "react-router-dom";
import type {
  TrialInvite,
  TrialInviteStatus,
} from "../../../services/trialInviteApi";
import { formatTrialDays } from "../../../utils/trialDays";
import { formatDateTime } from "./formatDateTime";
import { inviteContext } from "./inviteContext";

const STATUS_LABELS: Record<TrialInviteStatus, string> = {
  active: "Aktywny",
  used: "Użyty",
  expired: "Wygasł",
};

const STATUS_CLASSES: Record<TrialInviteStatus, string> = {
  active: "bg-green-100 text-green-800",
  used: "bg-neutral-100 text-neutral-700",
  expired: "bg-yellow-100 text-yellow-800",
};

/**
 * The trial order is created right after the registration commits, so for a
 * moment a fresh tenant has no subscription. Flag it only after this delay.
 */
const SUBSCRIPTION_MISSING_GRACE_MS = 2 * 60 * 1000;

const isSubscriptionMissing = (invite: TrialInvite): boolean =>
  invite.subscriptionMissing === true &&
  invite.usedAt !== null &&
  Date.now() - new Date(invite.usedAt).getTime() >=
    SUBSCRIPTION_MISSING_GRACE_MS;

interface InvitesTableProps {
  invites: TrialInvite[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Id of the invite whose revoke request is in flight. */
  revokingId: string | null;
  onRevoke: (invite: TrialInvite) => void;
}

const COLUMNS = [
  "Utworzono",
  "E-mail",
  "Okres próbny",
  "Ważny do",
  "Status",
  "Tenant",
  "Akcje",
];

const MessageRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <tr>
    <td
      colSpan={COLUMNS.length}
      className="px-4 py-6 text-center text-neutral-500"
    >
      {children}
    </td>
  </tr>
);

const SubscriptionMissing: React.FC<{ invite: TrialInvite }> = ({
  invite,
}) => {
  const helpId = `trial-invite-subscription-missing-help-${invite.id}`;
  return (
    <>
      <span
        className="ml-2 inline-block px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
        data-testid="trial-invite-subscription-missing"
        aria-describedby={helpId}
      >
        Brak subskrypcji
      </span>
      <p id={helpId} className="mt-1 max-w-xs text-xs text-red-800">
        Zamówienie trialu się nie utworzyło (log TRIAL_INVITE_ORDER_FAILED).
        Ratunek w panelu tenanta: „Stwórz zamówienie” z odznaczonym
        auto-odnawianiem, „Wyceń ręcznie” na 0 PLN, „Potwierdź i realizuj” (bez
        proformy, faktury i maila). Bez odznaczenia system wystawi proformę 7 dni
        przed końcem. Subskrypcja będzie aktywna, nie trial: klient nie zobaczy
        „Kupuję”, kupi włączając auto-odnawianie w ustawieniach subskrypcji. Nowe
        zaproszenie nie pomoże (konto już istnieje).{" "}
        {invite.usedByTenantId && (
          <Link
            to={`/admin/tenants/${invite.usedByTenantId}`}
            className="underline"
          >
            Otwórz tenanta
          </Link>
        )}
      </p>
    </>
  );
};

const RevokeButton: React.FC<{
  invite: TrialInvite;
  isRevoking: boolean;
  onRevoke: (invite: TrialInvite) => void;
}> = ({ invite, isRevoking, onRevoke }) => (
  <button
    type="button"
    onClick={() => onRevoke(invite)}
    disabled={isRevoking}
    aria-label={`${isRevoking ? "Odwołuję" : "Odwołaj"} zaproszenie ${inviteContext(invite)}`}
    className="px-3 py-1 text-sm border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50"
  >
    {isRevoking ? "Odwołuję..." : "Odwołaj"}
  </button>
);

const InvitesTable: React.FC<InvitesTableProps> = ({
  invites,
  isLoading,
  isError,
  onRetry,
  revokingId,
  onRevoke,
}) => (
  <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-x-auto">
    {isError && (
      <div
        role="alert"
        className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-red-50 border-b border-red-200 text-sm text-red-800"
      >
        <span>
          Nie udało się wczytać listy zaproszeń. Lista poniżej może być
          niepełna.
        </span>
        <button
          type="button"
          onClick={onRetry}
          className="px-3 py-1 border border-red-300 rounded-md bg-white hover:bg-red-100"
        >
          Spróbuj ponownie
        </button>
      </div>
    )}
    <p aria-live="polite" className="sr-only">
      {isLoading ? "Ładowanie listy zaproszeń..." : ""}
    </p>
    <table className="w-full text-sm" aria-busy={isLoading}>
      <caption className="sr-only">Zaproszenia na okres próbny</caption>
      <thead className="bg-neutral-50 text-left text-neutral-600">
        <tr>
          {COLUMNS.map((column) => (
            <th key={column} scope="col" className="px-4 py-2">
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <MessageRow>Ładowanie...</MessageRow>
        ) : invites.length === 0 ? (
          <MessageRow>{isError ? "—" : "Brak zaproszeń."}</MessageRow>
        ) : (
          invites.map((invite) => (
            <tr key={invite.id} className="border-t border-neutral-100">
              <td className="px-4 py-2">{formatDateTime(invite.createdAt)}</td>
              <td className="px-4 py-2">{invite.email ?? "—"}</td>
              <td className="px-4 py-2">
                {formatTrialDays(invite.trialDays)}
              </td>
              <td className="px-4 py-2">{formatDateTime(invite.expiresAt)}</td>
              <td className="px-4 py-2">
                <span
                  className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_CLASSES[invite.status]}`}
                >
                  {STATUS_LABELS[invite.status]}
                </span>
                {isSubscriptionMissing(invite) && (
                  <SubscriptionMissing invite={invite} />
                )}
              </td>
              <td className="px-4 py-2">
                {invite.usedByTenantId ? (
                  <Link
                    to={`/admin/tenants/${invite.usedByTenantId}`}
                    aria-label={`Otwórz tenanta (zaproszenie ${inviteContext(invite)})`}
                    className="text-primary-700 underline"
                  >
                    Otwórz
                  </Link>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-4 py-2">
                {invite.status === "active" ? (
                  <RevokeButton
                    invite={invite}
                    isRevoking={revokingId === invite.id}
                    onRevoke={onRevoke}
                  />
                ) : (
                  "—"
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export default InvitesTable;
